<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>Ajoutez une ou plusieurs références à la liste, puis enregistrez-les en une seule fois.</span>
      </div>
      <div class="ov-header__actions">
        <span v-if="references.length > 0" class="ov-badge ov-badge--neutral">
          {{ references.length }} en attente
        </span>
        <button type="button" class="ov-btn ov-btn--primary" @click="drawerOpen = true">
          <v-icon small>mdi-plus</v-icon>
          Nouvelle référence
        </button>
      </div>
    </div>

    <div class="ov-card">
      <div class="ov-card__head">
        <div>
          <div class="ov-card__kicker">En attente d'enregistrement</div>
          <div class="ov-card__title">
            {{ $t('reference.createdReferences') }}
            <span class="ov-count">{{ references.length }}</span>
          </div>
        </div>
      </div>

      <!-- État vide -->
      <div v-if="references.length === 0" class="ov-card__body">
        <div class="ov-empty">
          <div class="cr-empty__title">{{ $t('reference.noReference') }}</div>
          <div>{{ $t('reference.startCreating') }}</div>
          <button type="button" class="ov-btn ov-btn--navy ov-btn--sm mt-4" @click="drawerOpen = true">
            <v-icon small>mdi-plus</v-icon>
            {{ $t('reference.add') }}
          </button>
        </div>
      </div>

      <v-data-table
        v-else
        :headers="tableHeaders"
        :items="references"
        :items-per-page="-1"
        class="ov-table"
        hide-default-footer
        no-data-text="Aucune référence"
      >
        <template v-slot:[`item.designation`]="{ item }">
          <span class="cr-designation">{{ item.designation }}</span>
        </template>

        <template v-slot:[`item.description`]="{ item }">
          <span class="cr-muted">{{ item.description || '—' }}</span>
        </template>

        <template v-slot:[`item.referenceFamilleDesignation`]="{ item }">
          <span class="ov-badge ov-badge--navy ov-badge--plain">{{ item.referenceFamilleDesignation }}</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="ov-row-actions">
            <button
              type="button"
              class="ov-icon-btn ov-icon-btn--danger"
              title="Retirer"
              @click="deleteReference(references.indexOf(item))"
            >
              <v-icon small>mdi-trash-can-outline</v-icon>
            </button>
          </div>
        </template>
      </v-data-table>

      <div v-if="references.length > 0" class="ov-card__foot">
        <button type="button" class="ov-btn ov-btn--primary" @click="apiCreateReference">
          <v-icon small>mdi-content-save-outline</v-icon>
          {{ $t('reference.save') }} ({{ references.length }})
        </button>
      </div>
    </div>

    <!-- Panneau de création (droite) -->
    <v-navigation-drawer
      v-model="drawerOpen"
      right
      temporary
      fixed
      width="420"
      class="ov-panel"
    >
      <div class="ov-panel__head">
        <div class="ov-card__title">{{ $t('reference.formTitle') }}</div>
        <button type="button" class="ov-icon-btn" title="Fermer" @click="drawerOpen = false">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <div class="ov-panel__body">
        <div class="ov-note ov-note--info">
          <v-icon small>mdi-information-outline</v-icon>
          <span>{{ $t('reference.requiredFields') }}</span>
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('reference.designation') }}<span class="ov-required">*</span></label>
          <input
            v-model="referenceObject.designation"
            type="text"
            class="ov-input"
            :placeholder="$t('reference.designationPlaceholder')"
          />
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('reference.description') }}</label>
          <textarea
            v-model="referenceObject.description"
            class="ov-textarea"
            :placeholder="$t('reference.descriptionPlaceholder')"
            rows="3"
          ></textarea>
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('reference.referenceFamily') }}<span class="ov-required">*</span></label>
          <select v-model="referenceObject.referenceFamilleDesignation" class="ov-select">
            <option :value="null" disabled>{{ $t('reference.selectFamily') }}</option>
            <option v-for="(rf, i) in referenceFamilleList" :key="i" :value="rf.designation">
              {{ rf.designation }}
            </option>
          </select>
        </div>
      </div>

      <div class="ov-panel__foot">
        <button type="button" class="ov-btn ov-btn--secondary" @click="cancelForm">
          {{ $t('reference.cancel') }}
        </button>
        <button type="button" class="ov-btn ov-btn--navy" @click="addNewReference(referenceObject)">
          <v-icon small>mdi-plus</v-icon>
          {{ $t('reference.add') }}
        </button>
      </div>
    </v-navigation-drawer>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_CREATE_REFERENCE,
  API_CREATE_REFERENCE_FAMILLE,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import {
  showErrorMessage,
  showSuccessMessage,
} from "@/modules/messages/messageProcess";
export default {
  name: "CreateOrEditReference",
  data() {
    return {
      overlay: false,
      errorMsg: null,
      successMsg: null,
      drawerOpen: false,

      referenceObject: {
        designation: null,
        description: null,
        referenceFamilleDesignation: null,
      },

      referenceDataToSend: {
        datas: [],
      },

      references: [],
      referenceFamilleList: [],

      tableHeaders: [
        { text: 'Désignation',      value: 'designation',                sortable: true  },
        { text: 'Description',      value: 'description',                sortable: false },
        { text: 'Famille',          value: 'referenceFamilleDesignation', sortable: true  },
        { text: 'Actions',          value: 'actions',                    sortable: false },
      ],
    };
  },

  methods: {
    // API CREATE REFERENCE FAMILY
    async apiCreateReference() {
      this.overlay = true;
      this.references.forEach((element) => {
        this.referenceDataToSend.datas.push(element);
      });
      await axios
        .post(API_CREATE_REFERENCE, this.referenceDataToSend, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.successMsg = response.data.status.message;
            showSuccessMessage();
            this.referenceDataToSend.datas = [];
          }
        })
        .catch((e) => {
          this.referenceDataToSend.datas = [];
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.references = [];
          this.referenceDataToSend.datas = [];
          this.overlay = false;
        });
    },

    // REINITIALISER LE FORMULAIRE DE CRÉATION DES REFERENCES
    reininitializeFormNewReference() {
      this.referenceObject.designation = null;
      this.referenceObject.description = null;
      this.referenceObject.referenceFamilleDesignation = null;
    },

    // ANNULER ET FERMER LE DRAWER
    cancelForm() {
      this.reininitializeFormNewReference();
      this.drawerOpen = false;
    },

    // AJOUTER UNE NOUVELLE REFERENCE
    addNewReference(reference) {
      const checkReference =
        this.referenceObject.designation != null &&
        this.referenceObject.designation !== "" &&
        this.referenceObject.referenceFamilleDesignation != null &&
        this.referenceObject.referenceFamilleDesignation !== "";

      if (!checkReference) {
        this.errorMsg = this.$t('reference.fillRequiredFields');
        showErrorMessage();
      } else {
        var referenceAdded = {
          designation: null,
          description: null,
          referenceFamilleDesignation: null,
        };
        referenceAdded.designation = reference.designation;
        referenceAdded.description = reference.description;
        referenceAdded.referenceFamilleDesignation =
          reference.referenceFamilleDesignation;
        this.references.push(referenceAdded);
        
        // Réinitialiser le formulaire pour permettre d'ajouter une autre référence
        this.reininitializeFormNewReference();
        
        // Message de succès
        this.successMsg = this.$t('reference.referenceAdded');
        showSuccessMessage();
        
        // NE PAS fermer le drawer pour permettre d'ajouter plusieurs références
        // Si vous voulez fermer le drawer après chaque ajout, décommentez la ligne suivante :
        // this.drawerOpen = false;
      }
    },

    //DELETE A REFERENCE
    deleteReference(position) {
      this.references.splice(position, 1);
    },

    // API CREATE REFERENCE FAMILY
    async apiGetReferenceFamily() {
      await axios
        .get(API_CREATE_REFERENCE_FAMILLE)
        .then((response) => {
          if (response.data.status.code == 800) {
            this.referenceFamilleList = response.data.items;
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {});
    },
  },

  mounted() {
    this.apiGetReferenceFamily();
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/admin/ui";

.cr-designation {
  font-weight: 700;
  color: $ov-navy;
}

.cr-muted {
  color: $ov-muted;
}

.cr-empty__title {
  font-size: 15px;
  font-weight: 800;
  color: $ov-navy;
  margin-bottom: 4px;
}
</style>
